import { useState } from 'react';
import clsx from 'clsx';
import type { QueryResult } from '@idas/shared';
import { formatAxisDate, formatExact, formatNumber, type Point } from '../../lib/chart';

/**
 * The marks a dashboard tile is drawn from.
 *
 * All single-series on purpose. A saved query returns one measure, so identity
 * is never the job here — magnitude and trend are — which means one hue, taken
 * from the chart tokens rather than the UI accent. Those two differ: a fill is
 * judged against the chart surface, and the dark-mode step had to be re-chosen
 * rather than flipped.
 *
 * Every chart carries a hover layer. An SVG chart on a screen is interactive
 * whether or not anyone planned for it, and a reader who cannot get the exact
 * number out of a bar will go and re-run the query to find it.
 */

/**
 * Shared tooltip. Positioned in percentages because the SVG scales with its
 * container — pixel maths taken from the viewBox would drift as the tile
 * resizes.
 */
function Tip({ left, top, children }: { left: string; top: string; children: React.ReactNode }) {
  return (
    <div
      className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded border border-edge bg-surface-raised px-1.5 py-1 text-2xs leading-tight text-ink shadow-lg"
      style={{ left, top }}
    >
      {children}
    </div>
  );
}

/**
 * The single number a tile leads with.
 *
 * Deliberately not a one-bar chart: a lone value has nothing to compare
 * against, so the bar would encode nothing and only cost space.
 */
export function StatTile({
  label,
  value,
  masked,
}: {
  label: string;
  value: number;
  masked: boolean;
}) {
  return (
    <div className="flex h-full flex-col items-start justify-center px-1">
      <div
        className="font-semibold tabular-nums leading-none text-ink"
        style={{ fontSize: 'clamp(28px, 12cqw, 52px)' }}
        title={formatExact(value)}
      >
        {formatNumber(value)}
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <span className="truncate text-2xs text-ink-muted">{label}</span>
        {masked && (
          <span className="chip border-lock/30 bg-lock/10 text-lock">masked</span>
        )}
      </div>
    </div>
  );
}

/**
 * Two to four headline numbers, side by side.
 *
 * Each is its own answer, so they are set as figures rather than plotted
 * against each other — a bar chart here would invite a comparison the data
 * does not support (a total and its own subset are not peers).
 */
export function KpiRow({
  items,
}: {
  items: Array<{ label: string; value: number; masked: boolean }>;
}) {
  return (
    <div className="flex h-full items-center gap-3">
      {items.map((item, i) => (
        <div
          key={item.label}
          className={clsx(
            'min-w-0 flex-1',
            i > 0 && 'border-l border-edge pl-3',
          )}
        >
          <div
            className="truncate font-semibold tabular-nums leading-none text-ink"
            style={{ fontSize: 'clamp(18px, 7cqw, 34px)' }}
            title={formatExact(item.value)}
          >
            {formatNumber(item.value)}
          </div>
          <div className="mt-1.5 flex items-center gap-1">
            <span className="truncate text-2xs text-ink-muted" title={item.label}>
              {item.label}
            </span>
            {item.masked && <span className="text-lock" title="Masked by policy">•</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Horizontal bars — labels in a saved query are category names, and category
 * names are long. Vertical columns would turn them into rotated text.
 */
export function BarChart({ points, measure }: { points: Point[]; measure: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(...points.map((p) => p.value), 0);
  const rowHeight = 20;

  return (
    <div className="relative h-full overflow-y-auto pr-1">
      {points.map((point, i) => {
        const pct = max > 0 ? (point.value / max) * 100 : 0;
        return (
          <div
            key={`${point.label}-${i}`}
            className="group flex items-center gap-2 py-[3px]"
            style={{ minHeight: rowHeight }}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          >
            <span
              className="w-[38%] shrink-0 truncate text-right text-2xs text-ink-muted"
              title={point.label}
            >
              {point.label}
            </span>

            <div className="relative h-3 flex-1 rounded-sm bg-surface-sunken">
              <div
                className={clsx(
                  'absolute inset-y-0 left-0 rounded-sm bg-chart-mark transition-[width,opacity] duration-300',
                  hover !== null && hover !== i && 'opacity-45',
                )}
                style={{ width: `${Math.max(pct, point.value > 0 ? 1.5 : 0)}%` }}
              />
            </div>

            {/* Direct-labelled rather than hidden behind an axis: with a dozen
                rows the number is what the reader came for. */}
            <span className="w-12 shrink-0 text-right text-2xs tabular-nums text-ink">
              {formatNumber(point.value)}
            </span>
          </div>
        );
      })}

      {hover !== null && points[hover] && (
        <div className="sr-only" aria-live="polite">
          {points[hover]!.label}: {formatExact(points[hover]!.value)} {measure}
        </div>
      )}
    </div>
  );
}

/** Trend. One 2px line, a faint area beneath it, and an emphasised endpoint. */
export function LineChart({ points, measure }: { points: Point[]; measure: string }) {
  const [hover, setHover] = useState<number | null>(null);

  const W = 300;
  const H = 100;
  const PAD = { t: 8, r: 6, b: 16, l: 6 };

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;

  const x = (i: number) =>
    PAD.l + (i / Math.max(points.length - 1, 1)) * (W - PAD.l - PAD.r);
  const y = (v: number) => PAD.t + (1 - (v - min) / span) * (H - PAD.t - PAD.b);

  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.value)}`).join(' ');
  const area = `${line} L${x(points.length - 1)},${H - PAD.b} L${x(0)},${H - PAD.b} Z`;
  const last = points[points.length - 1]!;

  return (
    <div className="relative h-full">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="h-full w-full"
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label={`${measure} over time, ${points.length} points`}
      >
        {/* Baseline only. A full grid competes with a 2px line at this size. */}
        <line
          x1={PAD.l} y1={H - PAD.b} x2={W - PAD.r} y2={H - PAD.b}
          className="stroke-chart-grid" strokeWidth="1"
        />

        <path d={area} className="fill-chart-mark/10" />
        <path
          d={line}
          className="stroke-chart-mark"
          strokeWidth="2"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />

        {/* The endpoint is the value people look for first. */}
        <circle
          cx={x(points.length - 1)} cy={y(last.value)} r="3.5"
          className="fill-chart-mark stroke-surface" strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />

        {hover !== null && (
          <g>
            <line
              x1={x(hover)} y1={PAD.t} x2={x(hover)} y2={H - PAD.b}
              className="stroke-chart-mark/40" strokeWidth="1" vectorEffect="non-scaling-stroke"
            />
            <circle
              cx={x(hover)} cy={y(points[hover]!.value)} r="4"
              className="fill-chart-mark stroke-surface" strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        )}

        {/* Hit targets are wider than the marks — a 2px line is impossible to
            hover accurately. */}
        {points.map((_, i) => (
          <rect
            key={i}
            x={x(i) - (W / points.length) / 2}
            y={0}
            width={W / points.length}
            height={H}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
          />
        ))}
      </svg>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-between px-1 text-2xs text-ink-faint">
        <span>{formatAxisDate(points[0]!.raw)}</span>
        <span>{formatAxisDate(last.raw)}</span>
      </div>

      {hover !== null && points[hover] && (
        <Tip
          left={`${(x(hover) / W) * 100}%`}
          top={`${(y(points[hover]!.value) / H) * 100}%`}
        >
          <div className="font-medium">{formatExact(points[hover]!.value)}</div>
          <div className="text-ink-muted">{formatAxisDate(points[hover]!.raw)}</div>
        </Tip>
      )}
    </div>
  );
}

/** The honest fallback: a result whose shape does not name a chart. */
export function MiniTable({ result }: { result: QueryResult }) {
  const columns = result.columns.slice(0, 4);
  const rows = result.rows.slice(0, 6);

  return (
    <div className="h-full overflow-auto">
      <table className="w-full border-collapse text-2xs">
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.name}
                className="sticky top-0 truncate border-b border-edge bg-surface-raised px-1.5 py-1 text-left font-medium text-ink-muted"
              >
                {c.name}
                {c.masked && <span className="ml-1 text-lock">•</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-edge/50">
              {columns.map((_, j) => (
                <td key={j} className="max-w-[10rem] truncate px-1.5 py-1 text-ink">
                  {row[j] === null ? <span className="text-ink-faint">null</span> : String(row[j])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
